/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useCreateArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useCreateArbeitsbereichStation/useCreateArbeitsbereichStation';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useCreateAndUpdateSchema } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/formSchema/useCreateAndUpdateSchema';
import { useAppForm } from '@/components/form';
import { getDefaultVon } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/utils/getDefaultVon';
import z from 'zod';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { Standort } from '@/features/standortContext/entities/standort';
import { Standort as RotationsplanStandort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { getErinnerungsZeitFromFristDatum } from '@/domain/mitarbeiter/shared/utils/getErinerungsZeitFromFristDatum/getErinnerungsZeitFromFristDatum';

export function useForm({
  rotationsplan,
  currentStandort,
  abschnittId,
}: {
  rotationsplan: Rotationsplan;
  abschnittId: string;
  currentStandort: Standort;
}) {
  const [createArbeitsbereichStation] = useCreateArbeitsbereichStation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const schema = useCreateAndUpdateSchema();

  async function handleSubmit({
    value,
    abschnittId,
    rotationsplanId,
  }: {
    value: z.infer<typeof schema>;
    abschnittId: string;
    rotationsplanId: string;
  }) {
    try {
      const result = await createArbeitsbereichStation({
        input: {
          abschnittId,
          arbeitsbereichId: value.arbeitsbereich.id,
          lernziele: value.lernziele,
          von: dayjs(value.von).format('YYYY-MM-DD'),
          bis: dayjs(value.bis).format('YYYY-MM-DD'),
          rotationsplanId,
          abteilungsleiterId: value.abteilungsleiter?.id ?? undefined,
          frist: value.fristErinnerung
            ? {
                erinnerungAn: value.erinnerungAn,
                erinnerungsZeit: value.erinnerungsZeit.map((zeit) =>
                  getErinnerungsZeitFromFristDatum(
                    zeit,
                    dayjs(value.von).format('YYYY-MM-DD'),
                  ),
                ),
              }
            : undefined,
        },
      });

      const success =
        result.data.mitarbeiter_createArbeitsbereichStation.success;

      if (success?.station) {
        navigate(`/mitarbeiter/rotationsplan/${rotationsplanId}`);
        toast({
          title: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.success',
          ),
        });
        return;
      }

      if (success?.fristResult.error) {
        toast({
          title: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.frist.createError',
          ),
        });
        return;
      }

      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.error',
        ),
      });
    } catch (error) {
      Sentry.captureException(error);
      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.error',
        ),
      });
    }
  }

  const form = useAppForm({
    defaultValues: (() => {
      const defaultVon = getDefaultVon(rotationsplan);
      const defaultBis = dayjs(defaultVon).add(7, 'day').toDate();

      return {
        standort: new RotationsplanStandort({
          id: currentStandort.id,
          name: currentStandort.name,
          storenummer: currentStandort.storeNummer,
        }),
        von: defaultVon,
        bis: defaultBis,
        lernziele: '',
        arbeitsbereich: undefined,
        abteilungsleiter: undefined,
        fristErinnerung: false,
        erinnerungsZeit: [],
        erinnerungAn: [],
      } satisfies z.infer<typeof schema> as z.infer<typeof schema>;
    })(),
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value }) =>
      await handleSubmit({
        value,
        abschnittId,
        rotationsplanId: rotationsplan.id,
      }),
  });

  return form;
}
